/**
 * Compact board summary (#17) — answer line, clickable counts, fold for help.
 * Each caption has an “i” that opens what the figure means, so the strip stays
 * short without losing the explanations.
 */

import { addDays, formatList, type Money } from '@fareast/shared'
import type { ReactNode } from 'react'

import { Fold } from '../../../components/portal/Disclosure.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { BoardScrollNote } from '../../../components/portal/Board.js'
import { Answer, HeroFigure, Prose, SubFigure } from '../../../components/portal/Text.js'
import { HeroCapInfo } from '../../../components/studio/HeroCapInfo.js'
import { useFormat } from '../../../format/useFormat.js'
import { BOARD_MAX_COLUMNS, type TasksSummary } from './board.js'

/** The window `readmodels/tasks-board.ts` `BOARD_WEEK_DAYS` counts, today included. */
const WEEK_DAYS = 7

export type BoardHeroFilters = {
  readonly overdue: () => void
  readonly blocked: () => void
  /** Clears due / blocked facets so the Open count matches the list again. */
  readonly open: () => void
  readonly week: () => void
  /** Status = review — money / person reviews waiting on the studio. */
  readonly review: () => void
}

export type BoardHeroProps = {
  /**
   * `tasks_summary` asked with the page's filters: every figure here is its count of
   * the tasks those filters match (session 42), never a count of the rows on screen.
   */
  readonly summary: TasksSummary
  /**
   * The columns whose `past_due` the answer sentence names: `tasks_summary` asked
   * with nothing, because which accounts are behind on money is not something a
   * task filter changes.
   */
  readonly accounts: TasksSummary['clients']
  /**
   * How many of those tasks are in `review`: one more `tasks_summary`, the same
   * filters and `status: review`, because the read model's five figures do not carry
   * it (#18). Undefined while it loads.
   */
  readonly reviewCount: number | undefined
  /** How many columns the board below has. Decides whether it owes a scroll note. */
  readonly columns: number
  /** True when the board is showing and would once have scrolled sideways. Always off. */
  readonly scrolls: boolean
  /**
   * Which of the two views is under this region — kept for callers; the tip that
   * described the *other* view was removed to shrink the hero (#17).
   */
  readonly view: 'board' | 'list'
  /** The instant the "due this week" window is drawn from. */
  readonly at: Date
  /** Press a figure to set the matching list/board facet. */
  readonly onFilter: BoardHeroFilters
}

export function BoardHero({
  summary,
  accounts,
  reviewCount,
  columns,
  scrolls,
  at,
  onFilter,
}: BoardHeroProps) {
  const format = useFormat()
  const counts = summary.counts
  const dueCap = `Due ${format.date(at, 'day')}–${format.date(addDays(at, WEEK_DAYS - 1), 'day')}`
  const openTip =
    'Live tasks that are not done or cancelled, under the filters you have on. Clear the ' +
    'filters to count every one.'
  const extrasTip =
    counts.extras_in_flight_tasks === 0
      ? 'Billable extras that are approved, mid-delivery and not yet on an invoice. Nothing is in flight right now.'
      : extrasNote(counts.extras_in_flight_tasks, format.number)

  return (
    <div className="studio-hero studio-hero--tasks">
      <Region>
        <Answer>{answerSentence(summary, accounts)}</Answer>

        <RegionCols>
          <div>
            <HeroCapInfo
              label="Overdue"
              tip="Tasks past their due date that are not finished. Press the number to show only those."
            />
            <FilterTrigger label="Show overdue tasks" onClick={onFilter.overdue}>
              <HeroFigure value={counts.overdue} />
            </FilterTrigger>
          </div>

          <div>
            <HeroCapInfo
              label="Blocked on the client"
              tip="Waiting on the client — approvals, footage, credentials. Not our move. Press the number to show them."
            />
            <FilterTrigger label="Show tasks blocked on the client" onClick={onFilter.blocked}>
              <SubFigure value={counts.blocked_on_client} />
            </FilterTrigger>
          </div>

          <div>
            <HeroCapInfo
              label="Review"
              tip="Waiting on a person before the task can close — money reviews and other hand-offs. Press the number to show them."
            />
            <FilterTrigger label="Show tasks that need review" onClick={onFilter.review}>
              <SubFigure value={reviewCount ?? '—'} />
            </FilterTrigger>
          </div>

          <div>
            <HeroCapInfo label="Open" tip={openTip} />
            <FilterTrigger label="Clear due and blocked filters" onClick={onFilter.open}>
              <SubFigure value={counts.open} />
            </FilterTrigger>
          </div>

          <div>
            <HeroCapInfo
              label={dueCap}
              tip="Due today and the next six days, in the studio’s own zone. Press the number to show them."
            />
            <FilterTrigger label="Show tasks due this week" onClick={onFilter.week}>
              <SubFigure value={counts.due_this_week} />
            </FilterTrigger>
          </div>

          <div>
            <HeroCapInfo label="Extras" tip={extrasTip} />
            <ExtrasInFlight amounts={counts.extras_in_flight} />
          </div>
        </RegionCols>

        {scrolls ? (
          <BoardScrollNote>
            {format.number(columns)} client columns &mdash; scroll sideways &rarr;
          </BoardScrollNote>
        ) : null}

        <Fold summary="How a task bills, and what the colours mean">
          <Prose>
            A task carrying a money tag bills: an extra or a milestone lands on the client&rsquo;s
            next invoice once it is delivered, and a quote has to be answered before anything
            starts. A task with no money tag draws on an allowance inside the client&rsquo;s package
            and costs them nothing extra. Account work &mdash; re-scoping, billing chases, policy
            calls &mdash; we absorb.
          </Prose>
          <Prose>
            A red rail means genuinely broken: a task past its date, or a payment that failed. An
            ochre rail means the next move is the client&rsquo;s, not ours. Everything else is
            routine, and the card says so in words.
          </Prose>
          <Prose>
            The board is for a handful of clients. Past {String(BOARD_MAX_COLUMNS)} columns it warns
            when you ask for it: a row of {String(BOARD_MAX_COLUMNS)} scrolls sideways on most
            screens, the rest start a second row, on a phone the columns stack, and a board that
            wide is one nobody scans. The list shows the same tasks in one table.
          </Prose>
        </Fold>
      </Region>
    </div>
  )
}

/**
 * A hero / sub figure that sets a facet. `.th-sort` keeps the number looking like
 * the figure itself — no new button chrome.
 */
function FilterTrigger({
  label,
  onClick,
  children,
}: {
  readonly label: string
  readonly onClick: () => void
  readonly children: ReactNode
}) {
  return (
    <button type="button" className="th-sort" aria-label={label} onClick={onClick}>
      {children}
    </button>
  )
}

/**
 * The money on extras being delivered, one figure per currency.
 *
 * An empty list is a real answer — nothing is in flight — and it prints as an em
 * dash rather than as `$0.00`, which would be a currency this board did not pick.
 */
function ExtrasInFlight({ amounts }: { readonly amounts: readonly Money[] }) {
  if (amounts.length === 0) return <SubFigure value="—" />
  return (
    <>
      {amounts.map((amount) => (
        <SubFigure key={amount.currency} value={amount} />
      ))}
    </>
  )
}

/* ---------------------------------------------------------------- the words */

/**
 * The region's own conclusion — P1, "every panel opens with its conclusion".
 *
 * The mockup's sentence names the two accounts in trouble and says the trouble is
 * billing rather than delivery. The first half is a field (`past_due` on each
 * column); the second is a judgement nothing in `TasksBoardSchema` carries, so it is
 * not made here. What is said instead is the fact: who is past due on money.
 */
export function answerSentence(
  summary: TasksSummary,
  accounts: TasksSummary['clients'] = summary.clients,
): string {
  const pastDue = accounts.filter((one) => one.past_due === true).map((one) => one.name)

  if (pastDue.length === 0) {
    return summary.counts.overdue === 0
      ? 'Nothing on the board is late and no account is past due on money.'
      : 'No account on the board is past due on money; what is late is delivery work.'
  }

  const names = formatList(pastDue)
  if (pastDue.length === 1) {
    return `${names} is past due on money, so the trouble there is billing rather than delivery.`
  }
  const where = pastDue.length === 2 ? 'in both cases' : 'on each of them'
  return `${names} are past due on money, so the trouble ${where} is billing rather than delivery.`
}

/** "3 tasks — approved, delivering, unbilled". */
export function extrasNote(tasks: number, number: (value: number) => string): string {
  if (tasks === 0) return 'Nothing approved is mid-delivery and unbilled.'
  return `${number(tasks)} ${tasks === 1 ? 'task' : 'tasks'} — approved, delivering, unbilled`
}
